import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandlerFn,
  HttpInterceptorFn,
  HttpRequest,
  HttpResponse,
} from '@angular/common/http';
import { inject } from '@angular/core';
import { Observable, throwError, timer } from 'rxjs';
import { catchError, finalize, retry, tap, timeout } from 'rxjs/operators';

import { AuthService } from '@services/auth.service';
import { LoadingService } from '@services/loading.service';
import { ToastService } from '@services/toast.service';
import { extractErrorMessage } from '@utilities/http-errors';
import { SUPPRESS_ERROR_SNACKBAR, SUPPRESS_SUCCESS_SNACKBAR } from './snackbar-context';

const REQUEST_TIMEOUT_MS = 30_000;
const MAX_RETRIES = 2;
const BASE_BACKOFF_MS = 500;

const TRANSIENT_STATUSES = [0, 500, 502, 503, 504];

const PUBLIC_PATHS = [
  '/admin/auth/login',
  '/admin/auth/request-password-reset',
  '/admin/auth/process-password-reset',
];

function isPublic(url: string): boolean {
  return PUBLIC_PATHS.some((path) => url.includes(path));
}

function isSafeRead(req: HttpRequest<unknown>): boolean {
  return ['GET', 'HEAD', 'OPTIONS'].includes(req.method);
}

export const httpInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  const auth = inject(AuthService);
  const loading = inject(LoadingService);
  const toasts = inject(ToastService);

  loading.start();

  const token = auth.getToken();
  const request =
    token && !isPublic(req.url) && !req.headers.has('Authorization')
      ? req.clone({ setHeaders: { Authorization: `Bearer ${token}` } })
      : req;

  return next(request).pipe(
    timeout(REQUEST_TIMEOUT_MS),
    retry({
      count: isSafeRead(req) ? MAX_RETRIES : 0,
      delay: (error: unknown, attempt) => {
        const status = error instanceof HttpErrorResponse ? error.status : -1;
        if (!TRANSIENT_STATUSES.includes(status)) return throwError(() => error);
        return timer(BASE_BACKOFF_MS * 2 ** (attempt - 1));
      },
    }),
    tap((event) => {
      if (!(event instanceof HttpResponse)) return;
      if (isSafeRead(req) || req.context.get(SUPPRESS_SUCCESS_SNACKBAR)) return;

      const message = (event.body as { message?: unknown } | null)?.message;
      if (typeof message === 'string' && message) toasts.success(message);
    }),
    catchError((error: unknown) => {
      if (error instanceof HttpErrorResponse) {
        if (error.status === 401 && !isPublic(req.url)) {
          auth.logout();
          toasts.error('Your session has expired. Please sign in again.');
          return throwError(() => error);
        }
        if (!req.context.get(SUPPRESS_ERROR_SNACKBAR)) {
          toasts.error(extractErrorMessage(error));
        }
      }
      return throwError(() => error);
    }),
    finalize(() => loading.stop()),
  );
};
